import { useState, useRef, useEffect } from "react";
import type { GatewayProvider } from "../../../hooks/gateway/useGatewayProviders";
import type { GatewayConnectionType } from "../../../hooks/requests/gym";
import { useManagerGatewayConfig } from "../../../hooks/gym/useManagerGatewayConfig";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { ConfirmDialog } from "../../ui/confirm-dialog";
import { StepHelp } from "./providerConfigStepHelp";
import { StepCredentials } from "./providerConfigStepCredentials";
import { StepWebhooks } from "./providerConfigStepWebhooks";
import { OAuthConnectPanel, OAuthConnectedPanel } from "./providerConfigOAuth";

interface ProviderConfigDialogProps {
  provider: GatewayProvider;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  configured: boolean;
  onSaved: () => void;
  onDeleted: () => void;
  gymPublicId?: string;
  connectionType?: GatewayConnectionType;
  mpUserId?: number;
  webhookUrl?: string | null;
}

const TOTAL_STEPS = 3;

export const ProviderConfigDialog = ({
  provider,
  open,
  onOpenChange,
  configured,
  onSaved,
  onDeleted,
  gymPublicId,
  connectionType,
  mpUserId,
  webhookUrl,
}: ProviderConfigDialogProps) => {
  const [step, setStep] = useState<1 | 2 | 3>(configured ? 3 : 1);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const pendingDisconnect = useRef(false);

  const {
    saveConfig,
    isSaving,
    deleteConfig,
    isDeleting,
    connectMercadoPago,
    isConnecting,
  } = useManagerGatewayConfig();

  // Vista OAuth: conexión de un click cuando el provider lo soporta.
  // También para gyms con credenciales manuales activas: el panel ofrece
  // migrar a OAuth (reemplaza el blob y conserva el secret viejo en el
  // backend). "manualMode" abre la configuración avanzada (wizard de 3
  // pasos, cuyos campos manuales están @deprecated en gatewayProviders.ts).
  const oauthConnected = configured && connectionType === "oauth";
  const oauthConnect =
    provider.oauthAvailable === true && !oauthConnected && !manualMode;
  const isWizard = !oauthConnected && !oauthConnect;
  const manualConfigured = configured && !oauthConnected;

  useEffect(() => {
    if (open) {
      setStep(configured ? 3 : 1);
      setManualMode(false);
    }
  }, [open, configured]);

  const handleCredentialChange = (key: string, value: string) => {
    setCredentials((prev) => ({ ...prev, [key]: value }));
  };

  const allRequiredFieldsFilled = provider.credentialFields
    .filter((f) => f.required)
    .every((f) => (credentials[f.key] ?? "").trim().length > 0);

  const resetAndClose = () => {
    onOpenChange(false);
    setStep(configured ? 3 : 1);
    setCredentials({});
    setManualMode(false);
  };

  const handleSave = () => {
    if (!allRequiredFieldsFilled) return;

    const cleanCredentials: Record<string, string> = {};
    for (const field of provider.credentialFields) {
      const value = (credentials[field.key] ?? "").trim();
      if (value) {
        cleanCredentials[field.key] = value;
      }
    }

    saveConfig(
      { provider: provider.key, credentials: cleanCredentials },
      {
        onSuccess: () => {
          setStep(3);
          setCredentials({});
          onSaved();
        },
      },
    );
  };

  const handleDelete = () => {
    deleteConfig(provider.key, {
      onSuccess: () => {
        setShowDeleteConfirm(false);
        onDeleted();
        resetAndClose();
      },
    });
  };

  const handleDisconnectClick = () => {
    pendingDisconnect.current = true;
    onOpenChange(false);
    setTimeout(() => {
      if (pendingDisconnect.current) {
        pendingDisconnect.current = false;
        setShowDeleteConfirm(true);
      }
    }, 300);
  };

  const handleOpenChange = (value: boolean) => {
    if (!value && (isSaving || isDeleting)) return;
    if (!value) {
      setStep(configured ? 3 : 1);
      setCredentials({});
      setManualMode(false);
    }
    onOpenChange(value);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-10 sm:w-16 sm:h-12 rounded-xl bg-white border border-global-secondary flex items-center justify-center px-2 py-1.5 shrink-0">
                  <img
                    src={provider.logo}
                    alt={provider.displayName}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <DialogTitle>{provider.displayName}</DialogTitle>
              </div>
              {isWizard && (
                <div
                  className="flex items-center gap-1.5 mr-8"
                  role="group"
                  aria-label="Progreso"
                >
                  {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                    <div
                      key={i}
                      className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${step === i + 1 ? "bg-global-accent" : "bg-global-overlay-secondary"}`}
                      aria-current={step === i + 1 ? "step" : undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-4">
            {oauthConnected && (
              <OAuthConnectedPanel
                mpUserId={mpUserId}
                onDisconnect={handleDisconnectClick}
              />
            )}
            {oauthConnect && (
              <OAuthConnectPanel
                manualConfigured={manualConfigured}
                onManualMode={() => setManualMode(true)}
              />
            )}
            {isWizard && step === 1 && (
              <StepHelp provider={provider} gymPublicId={gymPublicId} />
            )}
            {isWizard && step === 2 && (
              <StepCredentials
                provider={provider}
                configured={configured}
                credentials={credentials}
                onCredentialChange={handleCredentialChange}
                onDisconnect={handleDisconnectClick}
              />
            )}
            {isWizard && step === 3 && gymPublicId && (
              <StepWebhooks
                provider={provider}
                gymPublicId={gymPublicId}
                backendWebhookUrl={webhookUrl}
                onUpdateCredentials={() => setStep(2)}
                onDisconnect={handleDisconnectClick}
              />
            )}
          </div>

          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-global-primary shrink-0">
            {oauthConnect && (
              <button
                type="button"
                onClick={() => connectMercadoPago()}
                disabled={isConnecting}
                className="w-full btn-compact btn-global-primary font-semibold uppercase tracking-wider active:scale-[0.98] shadow-global-button cursor-pointer transition-transform duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isConnecting ? "Conectando..." : "Conectar con Mercado Pago"}
              </button>
            )}
            {oauthConnected && (
              <button
                type="button"
                onClick={resetAndClose}
                className="w-full btn-compact btn-global-primary font-semibold uppercase tracking-wider active:scale-[0.98] shadow-global-button cursor-pointer transition-transform duration-150"
              >
                Listo
              </button>
            )}
            {isWizard && step === 1 && (
              <button
                type="button"
                onClick={() => setStep(2)}
                className="w-full btn-compact btn-global-primary font-semibold uppercase tracking-wider active:scale-[0.98] shadow-global-button cursor-pointer transition-transform duration-150"
              >
                Continuar
              </button>
            )}
            {isWizard && step === 2 && (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={isSaving}
                  className="flex-1 btn-compact btn-global-secondary cursor-pointer active:scale-[0.98] transition-transform duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Volver
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || !allRequiredFieldsFilled}
                  className="flex-1 btn-compact btn-global-primary font-semibold uppercase tracking-wider active:scale-[0.98] shadow-global-button cursor-pointer transition-transform duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            )}
            {isWizard && step === 3 && (
              <button
                type="button"
                onClick={resetAndClose}
                className="w-full btn-compact btn-global-primary font-semibold uppercase tracking-wider active:scale-[0.98] shadow-global-button cursor-pointer transition-transform duration-150"
              >
                Listo
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={(value) => {
          setShowDeleteConfirm(value);
          if (!value && !isDeleting) {
            onOpenChange(true);
            setStep(configured ? 3 : 2);
          }
        }}
        onConfirm={handleDelete}
        loading={isDeleting}
        title={`Desconectar ${provider.displayName}`}
        confirmMessage={`Esta accion eliminara las credenciales de ${provider.displayName} configuradas para este negocio.`}
        warningMessage="Los pagos online dejaran de funcionar inmediatamente."
        detailsContent={
          <p className="text-size-sm text-global-muted">
            Podras volver a configurar las credenciales en cualquier momento.
          </p>
        }
        confirmLabel="Desconectar"
        confirmLoadingText="Desconectando..."
      />
    </>
  );
};
